const schemaNormalizers = {
  // purge readonly phoneNumber
  "ui-personal_details": (schema: any): any => {
    return {
      ...schema,
      contact_phone_number: {
        ...schema.contact_phone_number,
        "ui:readonly": false
      }
    };
  },

  // TODO: why TF is this STEP????
  "ui-terms_and_conditions_step": (schema: any): any => {
    // delete ui:submitButtonOptions
    delete schema["ui:submitButtonOptions"];
    return schema;
  },

  // minLength for contact_phone_number
  "json-personal_details": (schema: any): any => {
    return {
      ...schema,
      properties: {
        ...schema.properties,
        contact_phone_number: {
          ...schema.properties.contact_phone_number,
          minLength: 10
        }
      }
    };
  }
};

export const normalizeSchema = ({
  type,
  name,
  schema
}: {
  type: "json" | "ui";
  name: string;
  schema: any;
}) => {
  const key = `${type}-${name}`;

  const normalizer = schemaNormalizers[key];
  return normalizer ? normalizer(schema) : schema;
};

import type {
  DataUrlItem,
  HistoryCalculationResponse,
  HousingArray
} from "./types";
import { dataURItoBlob } from "@rjsf/utils";
import { instance } from "api/apiBase";
import axios from "axios";
import config from "config/config";
import moment from "moment";

function isObject(obj: any): obj is Record<string, any> {
  return typeof obj === "object" && obj !== null;
}

export function findDataUrls(
  obj: any,
  currentPath: string | (string | number)[],
  files: DataUrlItem[]
): void {
  if (Array.isArray(obj))
    obj.forEach((item, index) => {
      const newPath = Array.isArray(currentPath) 
        ? currentPath.length > 0 ? [...currentPath, index] : [index]
        : currentPath ? [currentPath, index] : [index];
      findDataUrls(item, newPath, files);
    });
  else if (isObject(obj))
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const newPath = Array.isArray(currentPath)
          ? currentPath.length > 0 ? [...currentPath, key] : [key]
          : currentPath ? [currentPath, key] : [key];
        findDataUrls(obj[key], newPath, files);
      }
    }
  else if (typeof obj === "string" && obj.startsWith("data:")) {
    const dataUrlItem: DataUrlItem = {
      path: currentPath,
      dataUrl: obj
    };
    files.push(dataUrlItem);
  }
}

const uploadFileToS3Bucket = (s3Url: string, fileObj: unknown) => {
  return fetch(s3Url, {
    method: "PUT",
    // @ts-expect-error -- please, let me TEEEEEST.
    body: fileObj,
    mode: "cors"
  });
};

type Key = string | number;

// This is like lodash set
export function set<T>(obj: Record<Key, any>, _path: Key | Key[], value: T) {
  if (Object(obj) !== obj) return obj; // When obj is not an object
  let path = _path;
  // If not yet an array, get the keys from the string-path
  if (!Array.isArray(path)) path = path.toString().match(/[^.[\]]+/g) || [];
  path.slice(0, -1).reduce(
    (
      a,
      c,
      i // Iterate all of them except the last one
    ) =>
      Object(a[c]) === a[c] // Does the key exist and is its value an object?
        ? // Yes: then follow that path
          a[c]
        : // No: create the key. Is the next key a potential array-index?
          (a[c] =
            // eslint-disable-next-line no-bitwise -- i dunno what it doing
            Math.abs(Number(path[i + 1])) >> 0 === Number(path[i + 1])
              ? [] // Yes: assign a new array object
              : {}), // No: assign a new plain object
    obj
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- chatgpt, please...
  )[path[path.length - 1]] = value; // Finally assign the value to the last key
  return obj; // Return the top-level object to allow chaining
}

export const uploadLinks = async (
  dataUrlFiles: DataUrlItem[],
  applicantId?: string
) => {
  return Promise.all(
    dataUrlFiles.map(async ({ dataUrl, path }) => {
      const file = dataURItoBlob(dataUrl);

      const queryParams = new URLSearchParams({
        filename: file.name,
        type: file.blob.type
      }).toString();

      const { data: presignData } = await instance<{ url: string }>(
        `/portal/v1/boom_screen/assets/presign?${queryParams}`
      );

      const s3Res = await uploadFileToS3Bucket(presignData.url, file.blob);
      const metadata = {
        size: file.blob.size,
        original_filename: file.name,
        content_type: file.blob.type
      };

      const { data: assetData } = await axios.post<{
        asset: { global_id: string };
      }>(
        `${config.apiHost}/portal/v1/boom_screen/assets`,
        {
          applicant_id: applicantId,
          url: s3Res.url,
          metadata
        },
        { withCredentials: true }
      );

      return [path, assetData.asset.global_id];
    })
  );
};

const getNumberOfMonthsForHousingHistory = (
  hh: HousingArray,
  requiredMonths = 24
): HistoryCalculationResponse => {
  const errors: Error[] = [];

  if (hh.length === 0)
    return { accumulatedMonths: 0, errors, hasFullRequiredYears: false };

  const sortedHousing = [...hh].sort((a, b) => {
    const dateA =
      a.rent?.move_in_date || a.other?.move_in_date || a.own_home?.move_in_date;
    const dateB =
      b.rent?.move_in_date || b.other?.move_in_date || b.own_home?.move_in_date;

    if (!dateA || !dateB) return 0;

    return moment(dateB).valueOf() - moment(dateA).valueOf();
  });

  let accumulatedMonths = 0;
  let previousMoveOutDate = moment();
  let hasFullRequiredYears = false;

  const twoYearsAgo = moment().subtract(24, "months");

  let mostRecentMoveOutDate: moment.Moment | null = null;

  for (let i = 0; i < sortedHousing.length; i++) {
    const entity =
      sortedHousing[i].rent ||
      sortedHousing[i].other ||
      sortedHousing[i].own_home;

    if (!entity) continue;

    const moveInDate = moment(entity.move_in_date);
    const moveOutDate = entity.move_out_date
      ? moment(entity.move_out_date)
      : moment();

    if (moveInDate.isAfter(moment())) {
      errors.push(
        new Error(`Move-in date should be in the past for item number ${i + 1}`)
      );
      continue;
    }

    if (entity.move_out_date && !moveInDate.isBefore(moveOutDate)) {
      errors.push(
        new Error(
          `Move-in date should be before move-out date in item number ${i + 1}`
        )
      );
      continue;
    }

    if (!mostRecentMoveOutDate || moveOutDate.isAfter(mostRecentMoveOutDate))
      mostRecentMoveOutDate = moveOutDate;

    if (moveInDate.isAfter(previousMoveOutDate)) break;

    accumulatedMonths += previousMoveOutDate.diff(moveInDate, "months");
    previousMoveOutDate = moveInDate;

    if (accumulatedMonths >= requiredMonths) {
      hasFullRequiredYears = true;
      break;
    }
  }

  if (!mostRecentMoveOutDate || mostRecentMoveOutDate.isBefore(twoYearsAgo))
    hasFullRequiredYears = false;

  return { accumulatedMonths, errors, hasFullRequiredYears };
};

export const validateHousing = (formData: object, requiredMonth = 24) => {
  if ("address" in formData) {
    const hh: HousingArray = formData.address as HousingArray;

    return getNumberOfMonthsForHousingHistory(hh, requiredMonth);
  }
  return null;
};
